import { useQuery } from '@apollo/client';

import { GetPayByBankPointsBoostConfigDocument } from 'src/gql/graphql';
import { formatPointsMultiplier, resolvePointsBoostMultiplier } from './format-points-multiplier';

export type UsePayByBankPointsBoostOptions = {
  dispensaryId?: string;
  enabled?: boolean;
};

export type PayByBankPointsBoostStatus = 'available' | 'error' | 'loading' | 'unavailable';

export type UsePayByBankPointsBoostReturn = {
  status: PayByBankPointsBoostStatus;
  /** True only when status is 'available'. */
  boostEnabled: boolean;
  /**
   * Validated configured multiplier (e.g. 3 or 2.5), null unless available. This is the
   * configured offer only — it is not proof that any order earned points.
   */
  multiplier: number | null;
  /** Display label such as "2x" or "2.5x", null unless available. */
  multiplierLabel: string | null;
  loading: boolean;
};

const UNAVAILABLE_RESULT: UsePayByBankPointsBoostReturn = {
  status: 'unavailable',
  boostEnabled: false,
  multiplier: null,
  multiplierLabel: null,
  loading: false,
};

export function usePayByBankPointsBoost({
  dispensaryId,
  enabled = true,
}: UsePayByBankPointsBoostOptions): UsePayByBankPointsBoostReturn {
  const skip = !enabled || !dispensaryId;

  const { data, loading, error } = useQuery(GetPayByBankPointsBoostConfigDocument, {
    variables: { dispensaryId: dispensaryId ?? '' },
    fetchPolicy: 'network-only',
    skip,
  });

  if (skip) {
    return UNAVAILABLE_RESULT;
  }

  if (loading) {
    return { ...UNAVAILABLE_RESULT, status: 'loading', loading: true };
  }

  if (error) {
    return { ...UNAVAILABLE_RESULT, status: 'error' };
  }

  const config = data?.getDutchiePosLoyaltyConfig;
  const multiplier =
    config?.payByBankPointsBoostEnabled === true
      ? resolvePointsBoostMultiplier(config.payByBankPointsMultiplier)
      : null;

  if (multiplier === null) {
    return UNAVAILABLE_RESULT;
  }

  return {
    status: 'available',
    boostEnabled: true,
    multiplier,
    multiplierLabel: formatPointsMultiplier(multiplier),
    loading: false,
  };
}
